import api from "@services/axios/apiManager";

/**
 * 把「結帳時補登並通過 OTP 驗證的手機號碼」寫回平台帳號（customer.main_phone）。
 *
 * 只有 LINE 推播邀請註冊進來的客戶會走到：那條路徑沒有收過門號，帳號上的 main_phone 是空的，
 * 到結帳才第一次拿到號碼。後端會用 phoneVerifiedTicket 再驗一次「這支號碼確實剛通過 OTP」，
 * 前端不可能靠自己把任意號碼寫進帳號。
 *
 * 後端行為（POST /app-api/line/set-main-phone）：
 *   200 { main_phone, updated }  updated=false ＝帳號原本就有號碼（後端不覆寫）
 *   400 票證失效/過期            → 要客戶重新驗證
 *   409 號碼已被其他帳號使用      → 要客戶換一支
 */

/** 票證過期／失效（400）：驗證要重來一次。 */
export const SET_MAIN_PHONE_TICKET_EXPIRED_MSG = "手機驗證已過期，請重新驗證";
/** 門號已被其他帳號綁走（409）：只能換號。 */
export const SET_MAIN_PHONE_TAKEN_MSG = "這支手機已被其他帳號使用";
/** 其餘失敗（網路/5xx）：可直接重試。 */
export const SET_MAIN_PHONE_FAILED_MSG = "手機號碼儲存失敗，請稍後再試";

export type SetMainPhoneFailureKind = "ticket_expired" | "phone_taken" | "unknown";

export type SetMainPhoneResult =
  | { ok: true; mainPhone: string; updated: boolean }
  | { ok: false; kind: SetMainPhoneFailureKind; message: string };

interface SetMainPhoneBody {
  accessToken: string;
  phone: string;
  phoneVerifiedTicket: string;
}

/** 後端包裝層不一致（有的端點回 { data: {...} }、有的直接回物件）→ 兩種都讀得到。 */
const unwrap = (raw: unknown): { main_phone?: unknown; updated?: unknown } => {
  const body = (raw || {}) as { data?: unknown };
  const inner = body.data;
  return (inner && typeof inner === "object" ? inner : body) as { main_phone?: unknown; updated?: unknown };
};

const statusOf = (error: unknown): number | undefined =>
  (error as { response?: { status?: number } } | undefined)?.response?.status;

/**
 * 寫入帳號手機。不丟例外——呼叫端是結帳流程中的一個區塊，需要依 400/409 走不同的補救路徑，
 * 用回傳值分流比 try/catch 內再判斷 status 好讀。
 */
export async function setMainPhone({ accessToken, phone, phoneVerifiedTicket }: SetMainPhoneBody): Promise<SetMainPhoneResult> {
  try {
    const res = await api.post("/app-api/line/set-main-phone", { accessToken, phone, phoneVerifiedTicket }, { isAuth: false });
    const data = unwrap(res?.data);
    return {
      ok: true,
      // 後端沒回 main_phone（舊版/回應被中間層改形）時退回我們送出的號碼：
      // 這支號碼剛通過 OTP，拿它當顯示值不會出錯，總比讓畫面空白好。
      mainPhone: typeof data.main_phone === "string" && data.main_phone ? data.main_phone : phone,
      updated: data.updated !== false,
    };
  } catch (e) {
    const status = statusOf(e);
    if (status === 400) return { ok: false, kind: "ticket_expired", message: SET_MAIN_PHONE_TICKET_EXPIRED_MSG };
    if (status === 409) return { ok: false, kind: "phone_taken", message: SET_MAIN_PHONE_TAKEN_MSG };
    return { ok: false, kind: "unknown", message: SET_MAIN_PHONE_FAILED_MSG };
  }
}
